// eslint-disable-next-line @heidi/rules/no-new-legacy-session-data -- pre-existing recording hook importing only the StreamingStatus vocabulary from /contracts (the rule anchors on the first import, hence the placement)
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return -- type-aware lint resolves QuerySessionResponse/StreamingStatus as error via circular imports */
import { activeRecordingTransportAtom } from '@/atoms/chronicle';
import { requireBackupUploadModalAtom } from '@/atoms/sessionAtoms';
import {
  IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED,
  isGenerateConsultNoteFromDocumentTemplateEnabled,
} from '@/features/flags';
import {
  getFailedChunkThreshold,
  isSelfHealingStopEnabled,
} from '@/features/flags/self-healing-stop-flag';
import {
  DesktopCaptureError,
  DesktopCaptureFailure,
} from '@/lib/telemetry/desktopCaptureErrors';
import {
  finishRecordingControlAttempt,
  localCaptureTransport,
  setRecordingControlTransport,
  type RecordingControlOptions,
} from '@/lib/telemetry/recordingControlTracking';
import { UploadStatus } from '@/models/tauriRecordModel';
import { store, type Dispatch, type RootState } from '@/store';
import { sweepCaptureOnSessionEnd } from '@/tauri/online-chunk-upload/session-end-sweep';
import { isTauri } from '@/tauri/utils';
import chunkAudioRecorder from '@/utils/chunkAudioRecorder';
import { logger } from '@/utils/logger/logger';
import { isTranscribeLikeMode } from '@/utils/transcribeMode';
import { toast } from '@heidi/design-system/toast';
import { GenerationMethod, TemplateCategory } from '@heidi/schema/ml-scribe';
import {
  GET_SESSION_BY_ID,
  StreamingStatus,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import {
  beginArtifactGeneration,
  completeArtifactGeneration,
  resetArtifactGeneration,
  useQuerySessionById,
} from '@heidi/session/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSetAtom } from 'jotai';
import { get } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryTemplateById } from '../query/useQueryTemplateById';
import { GET_TRANSCRIPT_BY_SESSION_ID } from '../query/useQueryTranscriptBySessionId';
import {
  consumeRustChunkUploadPauseFinalization,
  hasOpenRustChunkUploadPauseFinalization,
  resolveChunkFailureThreshold,
} from './rustChunkUploadPauseFinalization';
import useMutateCreateDocumentFromNote from './useMutateCreateDocumentFromNote';
import { useMutateUpdateSessionSocketError } from './useMutateUpdateSessionSocketError';
import { usePersistAuthoritativeDuration } from './usePersistAuthoritativeDuration';

const STOP_SESSION_FROM_PAUSE = 'stopSessionFromPause';

export function useMutateStopRecordingFromPause({ sessionId }: { sessionId: string }) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<Dispatch>();
  const setUploadRequiredModal = useSetAtom(requireBackupUploadModalAtom);
  const setActiveRecordingTransport = useSetAtom(activeRecordingTransportAtom);
  const { mutate: markSessionHadWebsocketError } = useMutateUpdateSessionSocketError();
  const { mutate: createEmptyDocumentFromNote } = useMutateCreateDocumentFromNote({
    sessionId,
  });
  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        transcribe_mode: data.transcribe_mode,
        consult_note: data.consult_note,
        session_id: data.session_id,
        isSocketError: data.isSocketError,
        had_ws_recording_failure: data.had_ws_recording_failure,
        recording_status: data.recording_status,
      };
    },
  });

  // get the default template for the session if it exists
  const { data: template } = useQueryTemplateById(
    session?.consult_note?.template_id,
    session?.consult_note?.generation_method
  );

  const { chunks } = useSelector((rootState: RootState) => rootState.tauriRecordModel);
  const persistAuthoritativeDuration = usePersistAuthoritativeDuration({ sessionId });

  /**
   * Release the optimistic `UPLOADING` once nothing owns it. Every branch below
   * that surfaces the upload-required modal resolves successfully, dispatches
   * no backup upload and never hands off to note generation, so nothing else
   * releases it and the record button stays disabled behind a false
   * "Processing" overlay until restart.
   *
   * As in `useMutateStopRecording`, the value compare is not a version/epoch —
   * it cannot distinguish our stale optimistic write from a later owner's
   * legitimate `UPLOADING`. Safety rests on single-ownership-at-a-time: no
   * release site here dispatches note generation or a backup upload.
   */
  const releaseUploadingWhenUnowned = () => {
    completeArtifactGeneration({ kind: 'note', sessionId });
    queryClient.setQueryData<QuerySessionResponse>(
      [GET_SESSION_BY_ID, { sessionId }],
      (oldData) =>
        oldData?.streamingStatus === StreamingStatus.UPLOADING
          ? { ...oldData, streamingStatus: StreamingStatus.NOT_STARTED }
          : oldData
    );
  };

  return useMutation({
    mutationKey: [STOP_SESSION_FROM_PAUSE],
    networkMode: 'offlineFirst',
    // `void` keeps the argument optional for callers that open no attempt.
    mutationFn: async (input: RecordingControlOptions | void) => {
      const tracking = input ? input.tracking : undefined;
      setRecordingControlTransport(tracking, localCaptureTransport());
      // FR-038: this path ends the session and, until now, issued no native stop
      // at all — it assumed the earlier pause had already terminated capture.
      // That holds only if nothing restarted capture in the gap between the
      // pause and this End, which a device-change restart does. `onlySession`
      // because an active upload naming a DIFFERENT session is a different
      // patient's live recording: stopping it would be a new harm, not a fix.
      //
      // `stopInFlight` is what keeps this honest on the COMMON path. The native
      // probe answers `active` while the session is Stopping as well as Active,
      // so on an ordinary pause-then-End — where the pause's stop IPC is still
      // draining — an ungated sweep would either claim it caught a runaway or
      // report a failure on a stop that finishes fine moments later. Neither is
      // true and both are indistinguishable in the log from a real save. The
      // client already knows the difference, so we tell the helper rather than
      // teach the probe: an unsettled pause hand-off for this session IS a stop
      // in flight, and the sweep stands aside with its own logged reason.
      //
      // Awaited so capture is terminated before the session ends, and safe to
      // ignore: the helper never throws and never blocks the end on a failure.
      // DELIBERATELY NOT followed by `releaseRecordingStateAfterSweep`, unlike
      // the three creators. This is an ordinary user-driven End and the normal
      // stop path has ALREADY released every layer the helper would write,
      // before this line runs: `Timer.pause()` set `timerModel.isRunning` false,
      // `useRecordingButton`'s pause `onMutate` cleared `activeSessionIdAtom`
      // for every non-Chronicle input, and this mutation's own `onMutate`
      // cleared `activeRecordingTransportAtom`. A second release would be a
      // redundant double-write, and with a real `sessionId` it would take the
      // helper's `onlySession` branch — the one no other call site exercises —
      // to clear SHARED singletons for a claim already released. The remaining
      // layer, the cached `recording_status: PAUSED`, is owned by this path's
      // own status PATCH and its response, not by the sweep.
      const sweepResult = await sweepCaptureOnSessionEnd({
        target: { kind: 'onlySession', sessionId },
        callSite: 'endSessionFromPause',
        stopInFlight: hasOpenRustChunkUploadPauseFinalization(sessionId),
      });
      // recording_stop ends here: capture stopped at pause time, and the sweep
      // ended any capture restarted since. Finalization and note generation
      // below are not part of the attempt.
      const cachedDuration = queryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId },
      ])?.duration;
      const recordingDurationS =
        typeof cachedDuration === 'number' ? Math.round(cachedDuration) : 0;
      const metadata = recordingDurationS > 0 ? { recordingDurationS } : undefined;
      // A sweep stop that failed or went unconfirmed may leave capture live.
      if (
        sweepResult.attempted &&
        (sweepResult.outcome === 'error' || sweepResult.outcome === 'notConfirmed')
      ) {
        finishRecordingControlAttempt(tracking, {
          outcome: 'failed',
          error: new DesktopCaptureError(
            DesktopCaptureFailure.RECORDER_STOP_FAILED,
            'Session-end sweep could not confirm capture stopped'
          ),
          metadata,
        });
      } else {
        finishRecordingControlAttempt(tracking, { metadata });
      }

      // APP-7623: stop-from-pause never persisted duration — it relied on the
      // pause-time PATCH having succeeded. Re-persist here so a failed/missed
      // pause-time PATCH can't leave the session at duration=0 with chunks on
      // the server; awaited so it settles before note generation dispatches
      // below (the SCR-2711 backend gate reads the *server* duration).
      await persistAuthoritativeDuration();

      const shouldUseDocumentFlow =
        isTranscribeLikeMode(session?.transcribe_mode) &&
        ((!isGenerateConsultNoteFromDocumentTemplateEnabled() &&
          template?.template_category === TemplateCategory.DOCUMENT_TEMPLATE) ||
          (IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED() &&
            template?.template_category === TemplateCategory.FORM_TEMPLATE));
      // FR-019/022/034: imperative (non-hook) read, mirroring
      // useMutateStopRecording — see that file for rationale.
      const selfHealingEnabled = isTauri() && isSelfHealingStopEnabled();
      const failedChunkThreshold = selfHealingEnabled ? getFailedChunkThreshold() : 0;
      if (isTauri()) {
        // Locked at session start. When Rust owns chunk upload it owns
        // extraction/upload/backup/`:finish`, so `chunks.blobs` (the redux
        // tauriRecordModel populated by the legacy FE upload loop) is EMPTY on
        // this path. Mirrors useMutateStopRecording's locked-value read.
        const usingRustUpload = Boolean(
          store.getState().tauriRecordModel.useRustChunkUpload
        );

        if (usingRustUpload) {
          const pauseFinalization = consumeRustChunkUploadPauseFinalization(sessionId);
          if (!pauseFinalization) {
            logger.warn('DESKTOP_STOP_FROM_PAUSE_OUTCOME', {
              sessionId,
              path: 'rust-chunk-upload',
              pauseFinalization: 'missing',
              advancingToNoteGen: false,
            });
            markSessionHadWebsocketError(sessionId);
            setUploadRequiredModal(sessionId);
            releaseUploadingWhenUnowned();
            return;
          }

          const pauseFinalizationOutcome = await pauseFinalization;
          logger.debug('DESKTOP_STOP_FROM_PAUSE_OUTCOME', {
            sessionId,
            path: 'rust-chunk-upload',
            pauseFinalization: pauseFinalizationOutcome,
            advancingToNoteGen: pauseFinalizationOutcome === 'healthy',
          });
          if (pauseFinalizationOutcome !== 'healthy') {
            // Mark here so unhealthy outcomes from clear/supersede (incl. the
            // loud wedged-stop throw) still persist the failed-session flag —
            // the upload modal alone is not enough if End-session never ran
            // the main stop's unhealthy branch.
            markSessionHadWebsocketError(sessionId);
            setUploadRequiredModal(sessionId);
            releaseUploadingWhenUnowned();
          } else if (shouldUseDocumentFlow) {
            createEmptyDocumentFromNote({
              trigger: 'autonomous',
              generationMethod: GenerationMethod.TEMPLATE,
              templateCategory: template.template_category,
              templateId: template.id,
            });
            completeArtifactGeneration({ kind: 'note', sessionId });
          } else {
            dispatch.scribeRecordModel._actionPostRecording({ sessionID: sessionId });
          }
          return Promise.resolve();
        }

        // ── Legacy (non-Rust) chunk-upload path ──
        // The FE upload loop populated `chunks.blobs`, so it's meaningful here.
        const chunksIssues = chunks.blobs.filter(
          (chunk) => chunk.status === UploadStatus.Failed
        ).length;
        const allChunks = chunks.blobs.length;
        // FR-022/FR-022a/FR-034: shares the same flag-configurable tolerance
        // as the main stop path — flag OFF keeps today's hardcoded 3.
        const legacyFailedChunkThreshold = resolveChunkFailureThreshold(
          selfHealingEnabled,
          failedChunkThreshold
        );
        const chunksHadIssues =
          allChunks !== 0 &&
          (allChunks === chunksIssues || chunksIssues > legacyFailedChunkThreshold);
        if (chunksHadIssues) {
          markSessionHadWebsocketError(sessionId);
          setUploadRequiredModal(sessionId);
          releaseUploadingWhenUnowned();
        } else {
          if (shouldUseDocumentFlow) {
            createEmptyDocumentFromNote({
              trigger: 'autonomous',
              generationMethod: GenerationMethod.TEMPLATE,
              templateCategory: template.template_category,
              templateId: template.id,
            });
            completeArtifactGeneration({ kind: 'note', sessionId });
          } else {
            dispatch.scribeRecordModel._actionPostRecording({ sessionID: sessionId });
          }
        }
        return Promise.resolve();
      }

      const chunksHadIssues = chunkAudioRecorder?.chunkHasIssues();
      const shouldShowUploadBackupModal =
        session &&
        (session?.isSocketError || session?.had_ws_recording_failure || chunksHadIssues);

      if (shouldShowUploadBackupModal) {
        logger.debug('socket error in this session?: ' + session.isSocketError);
        logger.debug(
          'session had ws error previously: ' + session.had_ws_recording_failure
        );
        if (session?.isSocketError) {
          markSessionHadWebsocketError(session.session_id);
        }
        setUploadRequiredModal(sessionId);
        releaseUploadingWhenUnowned();
      } else {
        if (shouldUseDocumentFlow) {
          createEmptyDocumentFromNote({
            trigger: 'autonomous',
            generationMethod: template.internal
              ? GenerationMethod.INTERNAL_TEMPLATE
              : GenerationMethod.TEMPLATE,
            templateCategory: template.template_category,
            templateId: template.id,
          });
          completeArtifactGeneration({ kind: 'note', sessionId });
        } else {
          dispatch.scribeRecordModel._actionPostRecording({ sessionID: sessionId });
        }
        return;
      }
    },
    onMutate: () => {
      // Stop-from-pause is a real stop — release the transport stamp so the next
      // session routes by the input selector again (APP-7654 review follow-up).
      setActiveRecordingTransport(null);
      const previousSession = queryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId },
      ]);
      beginArtifactGeneration({
        key: { kind: 'note', sessionId },
        outgoingContent: previousSession?.consult_note?.result ?? null,
        phase: 'uploading',
      });
      // set session streaming status to uploading
      queryClient.setQueryData<QuerySessionResponse>(
        [GET_SESSION_BY_ID, { sessionId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          const newData: QuerySessionResponse = {
            ...oldData,
            streamingStatus: StreamingStatus.UPLOADING,
          };
          return newData;
        }
      );
      return { previousStreamingStatus: previousSession?.streamingStatus };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [GET_TRANSCRIPT_BY_SESSION_ID, { sessionId }],
      });
    },
    onError: (error, input, context) => {
      // Symmetry with useMutateStopRecording: no await in mutationFn can reject
      // today, so this is unreachable — but without it a future await would
      // silently reintroduce the stuck-UPLOADING soft-lock this hook just fixed.
      if (context) {
        queryClient.setQueryData<QuerySessionResponse>(
          [GET_SESSION_BY_ID, { sessionId }],
          (oldData) =>
            oldData
              ? { ...oldData, streamingStatus: context.previousStreamingStatus }
              : undefined
        );
        resetArtifactGeneration({ kind: 'note', sessionId });
      }
      // Still open only when the throw came before the capture boundary above.
      finishRecordingControlAttempt(input ? input.tracking : undefined, {
        outcome: 'failed',
        error,
      });
      logger.error('Error:', error);
      if (error instanceof AxiosError) {
        toast({
          tone: 'destructive',
          title: 'Failed to end session',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          description: get(error.response, 'data.detail.message', error.message),
        });
      }
    },
  });
}
